import React, { useEffect, Suspense, lazy as reactLazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './services/AuthContext';
import { ConfigProvider } from './services/configService';
import Navbar from './components/Navbar';
// Public routes — loaded eagerly (critical path)
import Login from './pages/Login';
import Register from './pages/Register';
import ProductList from './pages/ProductList';
import ProductDetail from './pages/ProductDetail';
import ProductRedirect from './components/ProductRedirect';
import SellerProfile from './pages/SellerProfile';
import ForgotPassword from './pages/ForgotPassword';
import './App.css';

// Wrap lazy() so a stale chunk after deploy reloads the page once instead of
// silently rendering nothing. Uses sessionStorage flag to avoid reload loops.
const lazy = (factory) => reactLazy(() => factory().catch((err) => {
  const msg = (err && (err.message || String(err))) || '';
  const isChunkErr = (err && err.name === 'ChunkLoadError') || /Loading chunk/i.test(msg) || /Loading CSS chunk/i.test(msg);
  if (!isChunkErr) throw err;
  const last = parseInt(sessionStorage.getItem('ml_lazy_retry_at') || '0', 10);
  if (Date.now() - last < 60000) throw err; // anti-loop within 60s
  sessionStorage.setItem('ml_lazy_retry_at', String(Date.now()));
  const u = new URL(window.location.href);
  u.searchParams.set('_r', String(Date.now()));
  window.location.replace(u.toString());
  return new Promise(() => {});
}));
// Authenticated routes — lazy loaded
const MyProducts    = lazy(() => import('./pages/MyProducts'));
const NewListing    = lazy(() => import('./pages/NewListing'));
const Profile       = lazy(() => import('./pages/Profile'));
const Messages      = lazy(() => import('./pages/Messages'));
const Conversation  = lazy(() => import('./pages/Conversation'));
const MyPurchases   = lazy(() => import('./pages/MyPurchases'));
const MyReviews     = lazy(() => import('./pages/MyReviews'));
const EditListing   = lazy(() => import('./pages/EditListing'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const Wishlist      = lazy(() => import('./pages/Wishlist'));
const MyInterests   = lazy(() => import('./pages/MyInterests'));
const StockManagement = lazy(() => import('./pages/StockManagement'));
const BulkUpload    = lazy(() => import('./pages/BulkUpload'));
const AgentFeed     = lazy(() => import('./pages/AgentFeed'));
const SellerDashboard = lazy(() => import('./pages/SellerDashboard'));
// Admin routes — lazy loaded
const AdminProducts      = lazy(() => import('./pages/admin/AdminProducts'));
const AdminLogs          = lazy(() => import('./pages/admin/AdminLogs'));
const AdminUsers         = lazy(() => import('./pages/admin/AdminUsers'));
const AdminBanners       = lazy(() => import('./pages/admin/AdminBanners'));
const AdminDashboard     = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminBlockedIps    = lazy(() => import('./pages/admin/AdminBlockedIps'));
const AdminProspects     = lazy(() => import('./pages/admin/AdminProspects'));
const AdminProspectDetail = lazy(() => import('./pages/admin/AdminProspectDetail'));
const AdminCrmDashboard  = lazy(() => import('./pages/admin/AdminCrmDashboard'));

function PageLoader() {
  return <div className="loading"><span className="spinner" /></div>;
}

function PrivateRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user?.roles?.includes('ADMIN')) return <Navigate to="/" replace />;
  return children;
}

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname + location.search;
    navigator.sendBeacon?.('/api/t?p=' + encodeURIComponent(p))
      || fetch('/api/t?p=' + encodeURIComponent(p), { method: 'POST', keepalive: true }).catch(() => {});
  }, [location]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
    <ConfigProvider>
    <AuthProvider>
      <Router>
        <div className="App">
          <PageTracker />
          <Navbar />
          <Routes>
            {/* Public — eagerly loaded */}
            <Route path="/" element={<div className="main-content"><ProductList /></div>} />
            <Route path="/producto/:slug" element={<ProductDetail />} />
            <Route path="/products/:id" element={<ProductRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/profile/:username" element={<SellerProfile />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* Authenticated — lazy loaded */}
            <Route path="/dashboard"        element={<PrivateRoute><Suspense fallback={<PageLoader />}><SellerDashboard /></Suspense></PrivateRoute>} />
            <Route path="/my-products"      element={<PrivateRoute><Suspense fallback={<PageLoader />}><MyProducts /></Suspense></PrivateRoute>} />
            <Route path="/new-listing"      element={<PrivateRoute><Suspense fallback={<PageLoader />}><NewListing /></Suspense></PrivateRoute>} />
            <Route path="/my-profile"       element={<PrivateRoute><Suspense fallback={<PageLoader />}><Profile /></Suspense></PrivateRoute>} />
            <Route path="/messages"         element={<PrivateRoute><Suspense fallback={<PageLoader />}><Messages /></Suspense></PrivateRoute>} />
            <Route path="/messages/:id"     element={<PrivateRoute><Suspense fallback={<PageLoader />}><Conversation /></Suspense></PrivateRoute>} />
            <Route path="/my-purchases"     element={<PrivateRoute><Suspense fallback={<PageLoader />}><MyPurchases /></Suspense></PrivateRoute>} />
            <Route path="/my-reviews"       element={<PrivateRoute><Suspense fallback={<PageLoader />}><MyReviews /></Suspense></PrivateRoute>} />
            <Route path="/products/:id/edit" element={<PrivateRoute><Suspense fallback={<PageLoader />}><EditListing /></Suspense></PrivateRoute>} />
            <Route path="/change-password"  element={<PrivateRoute><Suspense fallback={<PageLoader />}><ChangePassword /></Suspense></PrivateRoute>} />
            <Route path="/wishlist"         element={<PrivateRoute><Suspense fallback={<PageLoader />}><Wishlist /></Suspense></PrivateRoute>} />
            <Route path="/my-interests"     element={<PrivateRoute><Suspense fallback={<PageLoader />}><MyInterests /></Suspense></PrivateRoute>} />
            <Route path="/stock"            element={<PrivateRoute><Suspense fallback={<PageLoader />}><StockManagement /></Suspense></PrivateRoute>} />
            <Route path="/bulk-upload"      element={<PrivateRoute><Suspense fallback={<PageLoader />}><BulkUpload /></Suspense></PrivateRoute>} />
            <Route path="/agent"            element={<Suspense fallback={<PageLoader />}><AgentFeed /></Suspense>} />

            {/* Admin-only — lazy loaded */}
            <Route path="/admin"               element={<AdminRoute><Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense></AdminRoute>} />
            <Route path="/admin/products"      element={<AdminRoute><Suspense fallback={<PageLoader />}><AdminProducts /></Suspense></AdminRoute>} />
            <Route path="/admin/users"         element={<AdminRoute><Suspense fallback={<PageLoader />}><AdminUsers /></Suspense></AdminRoute>} />
            <Route path="/admin/logs"          element={<AdminRoute><Suspense fallback={<PageLoader />}><AdminLogs /></Suspense></AdminRoute>} />
            <Route path="/admin/banners"       element={<AdminRoute><Suspense fallback={<PageLoader />}><AdminBanners /></Suspense></AdminRoute>} />
            <Route path="/admin/security"      element={<AdminRoute><Suspense fallback={<PageLoader />}><AdminBlockedIps /></Suspense></AdminRoute>} />
            <Route path="/admin/prospects"     element={<AdminRoute><Suspense fallback={<PageLoader />}><AdminProspects /></Suspense></AdminRoute>} />
            <Route path="/admin/prospects/:id" element={<AdminRoute><Suspense fallback={<PageLoader />}><AdminProspectDetail /></Suspense></AdminRoute>} />
            <Route path="/admin/crm"           element={<AdminRoute><Suspense fallback={<PageLoader />}><AdminCrmDashboard /></Suspense></AdminRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
    </ConfigProvider>
    </HelmetProvider>
  );
}

export default App;
